import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import {PhoneHQAvatar, PhoneHQButton} from "ch3-ui-lib";
import { UserWithMatrixId } from './CreateRoomModal';

interface UserListToCreateRoomProps {
    prepareNewChat: boolean;
    usersToCreateChat: UserWithMatrixId[];
    setUsersToCreateChat: (users: UserWithMatrixId[]) => void;
    setPrepareNewChat: (prepare: boolean) => void;
    setIsCreatingGroup?: (isCreating: boolean) => void;
    getFullName: (firstName: string, lastName: string) => string;
    onCancel?: () => void;
}
const UserListToCreateRoom = ({
                                  prepareNewChat,
                                  usersToCreateChat,
                                  setUsersToCreateChat,
                                  setPrepareNewChat,
                                  setIsCreatingGroup,
                                  getFullName,
                                  onCancel,
                              }: UserListToCreateRoomProps) => {
    const [scrollOffset, setScrollOffset] = useState(0);
    const [maxVisibleUsers, setMaxVisibleUsers] = useState(6);

    useEffect(() => {
        const updateMaxVisibleUsers = () => {
            const width = window.innerWidth;
             if (width < 1024) {
                setMaxVisibleUsers(2);
            } else if (width < 1280) {
                setMaxVisibleUsers(3);
            } else if (width < 1536) {
                setMaxVisibleUsers(5);
            } else if (width < 1920) {
                setMaxVisibleUsers(6);
            }
        };

        updateMaxVisibleUsers();
        window.addEventListener('resize', updateMaxVisibleUsers);

        return () => window.removeEventListener('resize', updateMaxVisibleUsers);
    }, []);

    useEffect(() => {
        if (scrollOffset >= usersToCreateChat.length) {
            setScrollOffset(Math.max(0, usersToCreateChat.length - maxVisibleUsers));
        }
    }, [usersToCreateChat.length, scrollOffset, maxVisibleUsers]);

    const removeUser = (userToRemove: UserWithMatrixId) => {
        const newUsers = usersToCreateChat.filter((el: UserWithMatrixId) => el.userId !== userToRemove.userId);
        setUsersToCreateChat(newUsers);

        if (newUsers.length === 0) {
            setPrepareNewChat(false);
            setIsCreatingGroup?.(false);
            setScrollOffset(0);
            onCancel?.();
        } else if (scrollOffset > 0 && scrollOffset >= newUsers.length - maxVisibleUsers + 1) {
            setScrollOffset(Math.max(0, newUsers.length - maxVisibleUsers));
        }
    };

    const scrollLeft = () => {
        setScrollOffset(Math.max(0, scrollOffset - 1));
    };

    const scrollRight = () => {
        setScrollOffset(Math.min(usersToCreateChat.length - maxVisibleUsers, scrollOffset + 1));
    };

    const canScrollLeft = scrollOffset > 0;
    const canScrollRight = scrollOffset + maxVisibleUsers < usersToCreateChat.length;
    const needsScrolling = usersToCreateChat.length > maxVisibleUsers;

    const visibleUsers = usersToCreateChat.slice(scrollOffset, scrollOffset + maxVisibleUsers);

    return (
        <div>
            <div className="flex items-center text-tiny">
                {prepareNewChat && (
                    <div className="flex items-center gap-2 text-tiny min-w-0 flex-1">
                        <span className="flex-shrink-0">To:</span>

                        {/* Only show left scroll button if scrolling is needed and can scroll left */}
                        {needsScrolling && (
                            <div className="flex-shrink-0 w-8 items-center flex">
                                <PhoneHQButton
                                    onClick={scrollLeft}
                                    disabled={!canScrollLeft}
                                    variant={"tertiary"}
                                    className={'p-0 w-full'}
                                    style={{ opacity: canScrollLeft ? 1 : 0.3 }}
                                >
                                    <ChevronLeft size={20} />
                                </PhoneHQButton>
                            </div>
                        )}

                        <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                            {visibleUsers.map((user: UserWithMatrixId) => (
                                <PhoneHQButton
                                    key={user.userId}
                                    variant="tertiary"
                                    className="flex-shrink min-w-0 max-w-[250px]"
                                >
                                    <PhoneHQAvatar
                                        src={user.avatarUrl}
                                        name={getFullName(user.firstName, user.lastName)}
                                        size="20px"
                                        className="flex-shrink-0"
                                    />
                                    <span className="text-tiny font-semibold max-w-[100px] min-w-0 truncate">
                                        {getFullName(user.firstName, user.lastName)}
                                    </span>
                                    <X
                                        size={16}
                                        className="cursor-pointer hover:bg-surface-muted rounded flex-shrink-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeUser(user);
                                        }}
                                    />
                                </PhoneHQButton>
                            ))}
                        </div>

                        {/* Only show right scroll button if scrolling is needed and can scroll right */}
                        {needsScrolling && (
                            <div className="flex-shrink-0 w-8 items-center flex">
                                <PhoneHQButton
                                    onClick={scrollRight}
                                    disabled={!canScrollRight}
                                    variant={"tertiary"}
                                    className={'p-0 w-full'}
                                    style={{ opacity: canScrollRight ? 1 : 0.3 }}
                                >
                                    <ChevronRight size={20} />
                                </PhoneHQButton>
                            </div>
                        )}

                        {needsScrolling && (
                            <span className="text-ink-muted text-tiny flex-shrink-0 ml-2">
                                {scrollOffset + 1}-{Math.min(scrollOffset + maxVisibleUsers, usersToCreateChat.length)} of {usersToCreateChat.length}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserListToCreateRoom;